<template>
    <div class="moderation-log-container">
        <div class="page-header">
            <h2>审核拦截日志</h2>
        </div>

        <el-tabs v-model="activeTab" @tab-change="handleTabChange">
            <!-- 违规用户统计 Tab -->
            <el-tab-pane label="违规用户统计" name="users">
                <el-table v-loading="statsLoading" :data="userStats" style="width: 100%" border stripe>
                    <el-table-column label="用户" min-width="180">
                        <template #default="{ row }">
                            <div class="user-cell">
                                <el-avatar :size="32"
                                    :src="row.avatar || 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'" />
                                <div class="user-info">
                                    <span class="username">{{ row.username || '未知用户' }}</span>
                                    <span class="user-id">ID: {{ row.userId }}</span>
                                </div>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column label="违规总次数" width="120" sortable prop="totalCount">
                        <template #default="{ row }">
                            <el-tag :type="row.totalCount >= 10 ? 'danger' : row.totalCount >= 5 ? 'warning' : 'info'"
                                effect="dark" size="large">
                                {{ row.totalCount }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="帖子" width="80" align="center">
                        <template #default="{ row }">
                            <span v-if="row.postCount > 0" class="count-badge post">{{ row.postCount }}</span>
                            <span v-else class="count-zero">-</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="评论" width="80" align="center">
                        <template #default="{ row }">
                            <span v-if="row.commentCount > 0" class="count-badge comment">{{ row.commentCount }}</span>
                            <span v-else class="count-zero">-</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="聊天" width="80" align="center">
                        <template #default="{ row }">
                            <span v-if="row.chatCount > 0" class="count-badge chat">{{ row.chatCount }}</span>
                            <span v-else class="count-zero">-</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="最近违规" width="170">
                        <template #default="{ row }">
                            {{ formatDate(row.lastTime) }}
                        </template>
                    </el-table-column>
                </el-table>
            </el-tab-pane>

            <!-- 拦截日志明细 Tab -->
            <el-tab-pane label="拦截日志明细" name="logs">
                <div class="filter-bar">
                    <el-radio-group v-model="filterType" @change="fetchLogs">
                        <el-radio-button label="">全部</el-radio-button>
                        <el-radio-button label="post">帖子</el-radio-button>
                        <el-radio-button label="comment">评论</el-radio-button>
                        <el-radio-button label="chat">聊天</el-radio-button>
                    </el-radio-group>
                </div>

                <el-table v-loading="loading" :data="logs" style="width: 100%" border stripe>
                    <el-table-column label="ID" prop="id" width="70" />
                    <el-table-column label="用户" width="120">
                        <template #default="{ row }">
                            <span>{{ row.user?.username || '未知' }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="类型" width="90">
                        <template #default="{ row }">
                            <el-tag :type="getTypeTag(row.contentType)" size="small">
                                {{ getTypeLabel(row.contentType) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="被拦截内容" min-width="250">
                        <template #default="{ row }">
                            <div class="content-cell">
                                <el-tooltip :content="row.content" placement="top" :show-after="500">
                                    <span class="content-excerpt">{{ getExcerpt(row.content, 80) }}</span>
                                </el-tooltip>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column label="命中敏感词" width="200">
                        <template #default="{ row }">
                            <div class="matched-words">
                                <el-tag v-for="word in row.matchedWords.split(',')" :key="word" type="danger"
                                    size="small" effect="plain" style="margin: 2px;">
                                    {{ word }}
                                </el-tag>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column label="IP" prop="ip" width="130" />
                    <el-table-column label="时间" width="170">
                        <template #default="{ row }">
                            {{ formatDate(row.createdAt) }}
                        </template>
                    </el-table-column>
                </el-table>

                <div class="pagination-container">
                    <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize" :total="total"
                        layout="total, prev, pager, next" @current-change="fetchLogs" />
                </div>
            </el-tab-pane>
        </el-tabs>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import api from '@/utils/api'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'

const activeTab = ref('users')

// ---- 用户统计 ----
const statsLoading = ref(false)
const userStats = ref([])

const fetchUserStats = async () => {
    statsLoading.value = true
    try {
        const response = await api.get('/api/admin/moderation-user-stats')
        userStats.value = response.data.stats || []
    } catch (error) {
        ElMessage.error('获取违规用户统计失败')
    } finally {
        statsLoading.value = false
    }
}

// ---- 日志明细 ----
const loading = ref(false)
const logs = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(20)
const filterType = ref('')

const fetchLogs = async () => {
    loading.value = true
    try {
        const params = {
            page: currentPage.value,
            page_size: pageSize.value,
            content_type: filterType.value
        }
        const response = await api.get('/api/admin/moderation-logs', { params })
        logs.value = response.data.logs || []
        total.value = response.data.total
    } catch (error) {
        ElMessage.error('获取审核日志失败')
    } finally {
        loading.value = false
    }
}

const handleTabChange = (tab) => {
    if (tab === 'users' && userStats.value.length === 0) {
        fetchUserStats()
    } else if (tab === 'logs' && logs.value.length === 0) {
        fetchLogs()
    }
}

const getTypeTag = (type) => {
    const map = { 'post': 'warning', 'comment': 'info', 'chat': 'primary' }
    return map[type] || 'info'
}

const getTypeLabel = (type) => {
    const map = { 'post': '帖子', 'comment': '评论', 'chat': '聊天' }
    return map[type] || type
}

const getExcerpt = (content, maxLen = 80) => {
    if (!content) return ''
    return content.length > maxLen ? content.substring(0, maxLen) + '...' : content
}

const formatDate = (date) => {
    return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
}

onMounted(() => {
    fetchUserStats()
})
</script>

<style scoped>
.moderation-log-container {
    padding: 20px;
    background: #fff;
    border-radius: 8px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.filter-bar {
    margin-bottom: 16px;
}

.user-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.user-info {
    display: flex;
    flex-direction: column;
}

.username {
    font-weight: 500;
    font-size: 14px;
    color: #303133;
}

.user-id {
    font-size: 12px;
    color: #909399;
}

.count-badge {
    display: inline-block;
    min-width: 24px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    color: #fff;
}

.count-badge.post {
    background-color: #e6a23c;
}

.count-badge.comment {
    background-color: #909399;
}

.count-badge.chat {
    background-color: #409eff;
}

.count-zero {
    color: #c0c4cc;
}

.content-cell {
    max-width: 300px;
}

.content-excerpt {
    font-size: 13px;
    color: #606266;
    word-break: break-all;
}

.matched-words {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
}

.pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}
</style>
